if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // MF H3: clear any in-flight "Added" restore so rapid ATC cannot leave stale text
        this.clearMfAddedConfirmation(true);

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            this.error = false;
            // MF H3: mark success BEFORE cartUpdate publish. Subscribers (e.g. quantity
            // rules) can throw sync during publish(); that must not skip the Added state.
            this.mfAddSucceeded = true;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            } catch (e) {
              console.error(e);
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');

            // MF H3: brief "Added" on PDP after successful add only (drawer behavior unchanged)
            if (this.mfAddSucceeded) {
              this.mfAddSucceeded = false;
              // Show now, then re-assert after paint in case a sync cart subscriber overwrote the label
              this.showMfAddedConfirmation();
              requestAnimationFrame(() => {
                requestAnimationFrame(() => this.showMfAddedConfirmation());
              });
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      /**
       * MF H3 — PDP Add to Cart success confirmation.
       * Text-only "Added" ~1.2s; no motion/color. Skips quick-add modal.
       * prefers-reduced-motion: still shows the label (functional, not decorative motion).
       */
      isMfPdpAddToCart() {
        if (this.closest('quick-add-modal')) return false;
        // Prefer mf-product-actions wrapper; also accept main PDP submit id from buy-buttons.
        if (this.closest('.mf-product-actions')) return true;
        const id = this.submitButton && this.submitButton.id;
        return Boolean(id && id.indexOf('ProductSubmitButton-') === 0);
      }

      getMfSubmitLabel() {
        if (!this.submitButton) return null;
        // Direct child label span (avoid spinner/svg spans)
        return (
          this.submitButton.querySelector(':scope > span') ||
          this.submitButtonText ||
          this.submitButton.querySelector('span')
        );
      }

      clearMfAddedConfirmation(restoreLabel) {
        if (this.mfAddedTimer) {
          clearTimeout(this.mfAddedTimer);
          this.mfAddedTimer = null;
        }
        this.mfAddSucceeded = false;
        if (restoreLabel && this.isMfPdpAddToCart() && !this.error) {
          const label = this.getMfSubmitLabel();
          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (label && (!soldOutMessage || soldOutMessage.classList.contains('hidden'))) {
            label.textContent = window.variantStrings.addToCart;
            label.classList.remove('hidden');
          }
        }
      }

      showMfAddedConfirmation() {
        if (!this.isMfPdpAddToCart() || this.error) return;
        if (this.submitButton.classList.contains('loading')) return;

        const label = this.getMfSubmitLabel();
        if (!label) return;

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (soldOutMessage && !soldOutMessage.classList.contains('hidden')) return;

        // Keep a single restore timer; refresh label if already in Added state
        const restore = window.variantStrings.addToCart;
        const alreadyAdded = label.textContent.trim() === 'Added';
        label.classList.remove('hidden');
        label.textContent = 'Added';
        this.submitButtonText = label;

        if (alreadyAdded && this.mfAddedTimer) return;

        if (this.mfAddedTimer) {
          clearTimeout(this.mfAddedTimer);
          this.mfAddedTimer = null;
        }

        const durationMs = 1200;
        const startedAt = Date.now();
        this.mfAddedTimer = setTimeout(() => {
          this.mfAddedTimer = null;
          const current = this.getMfSubmitLabel();
          if (!current) return;
          if (this.submitButton.classList.contains('loading')) return;
          if (this.error) return;
          const soldOut = this.submitButton.querySelector('.sold-out-message');
          if (soldOut && !soldOut.classList.contains('hidden')) return;
          if (current.textContent.trim() === 'Added') {
            current.textContent = restore;
          }
        }, durationMs);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
